import { useSlidingIndicator } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'

import type { CampaignChip } from '../model/chips'

interface CampaignFilterChipsProps<TValue extends string> {
  chips: CampaignChip<TValue>[]
  active: TValue
  onChange: (value: TValue) => void
}

/** The counted pill row above a campaign list. Shared by Contact and Activity. */
export function CampaignFilterChips<TValue extends string>({
  chips,
  active,
  onChange,
}: CampaignFilterChipsProps<TValue>) {
  const { containerRef, register, box } = useSlidingIndicator<TValue>(active)

  return (
    <div
      ref={containerRef}
      role="tablist"
      className="relative flex flex-wrap items-center gap-2"
    >
      {/* The fill glides between chips instead of blinking on. It sits behind
          them, so the chips themselves stay transparent — a white chip would
          hide it mid-flight. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute rounded-full bg-warm-50 transition-[left,top,width,height] duration-200 ease-out"
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          opacity: box.ready ? 1 : 0,
        }}
      />
      {chips.map((chip) => {
        const selected = chip.key === active
        return (
          <button
            key={chip.key}
            ref={register(chip.key)}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(chip.key)}
            // Every chip keeps the same weight — only the colour changes on
            // selection. A weight swap re-measures the chip mid-slide and the
            // fill chases a target that is still moving.
            className={cn(
              'relative z-10 flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.25 text-xs font-medium transition-colors',
              selected
                ? 'border-transparent text-gray-900'
                : 'border-gray-200 hover:bg-gray-50',
              !selected && chip.alert === true && 'text-campaign-attention',
              !selected && chip.alert !== true && 'text-gray-500',
            )}
          >
            {chip.label}
            <span
              className={cn(
                selected
                  ? 'text-gray-500'
                  : chip.alert === true
                    ? 'text-campaign-attention'
                    : 'text-gray-300',
              )}
            >
              {chip.count}
            </span>
          </button>
        )
      })}
    </div>
  )
}
